import {
  AppointableDTO,
  FacilityDTO,
  UserStaffPublicDTO,
} from "@gymflow/types";
import { Duration } from "luxon";

import { AppointableFormValues } from "../types";

export const mapAppointableToInitialValues = (
  appointable: AppointableDTO,
): AppointableFormValues => {
  const durationOption = {
    id: appointable.duration,
    label: Duration.fromObject({ minutes: appointable.duration }).toHuman(),
    value: appointable.duration,
  };

  const blockExtraTimeOption =
    appointable.blockedTime !== undefined
      ? {
          id: appointable.blockedTime,
          label: Duration.fromObject({
            minutes: appointable.blockedTime,
          }).toHuman(),
          value: appointable.blockedTime,
        }
      : undefined;

  const bookingPossibleEveryOption = appointable.alignToInterval
    ? {
        id: appointable.alignToInterval,
        label: Duration.fromObject({
          minutes: appointable.alignToInterval,
        }).toHuman(),
        value: appointable.alignToInterval,
      }
    : undefined;

  const pricingStructure =
    appointable.pricingModel === "FREE" ? "FREE" : "FIXED_PRICE";

  const typeOfCost =
    appointable.sessionCost !== undefined && appointable.price !== undefined
      ? "BOTH"
      : appointable.sessionCost !== undefined
        ? "CREDITS"
        : "PRICE";

  const priceExTaxPrice =
    appointable.price && appointable.taxRate !== undefined
      ? Math.round(
          (appointable.price / (1 + appointable.taxRate / 100)) * 100,
        ) / 100
      : 0;

  const membersPriceExTaxPrice =
    appointable.membersOnlyPrice && appointable.taxRate !== undefined
      ? Math.round(
          (appointable.membersOnlyPrice / (1 + appointable.taxRate / 100)) *
            100,
        ) / 100
      : 0;

  const categoryOption = {
    id: appointable.appointableCategory.id,
    label: appointable.appointableCategory.name,
    value: appointable.appointableCategory.id,
  };

  const staffOptions = appointable.appointableHosts.map(
    (user: UserStaffPublicDTO) => ({
      id: user.id,
      value: user,
      label: `${user.firstName} ${user.lastName}`,
      picture: user?.picture,
    }),
  );

  const facilityOptions = appointable.appointableFacilities.map(
    (facility: FacilityDTO) => ({
      id: facility.id,
      value: facility,
      label: facility.name,
      picture: facility?.picture,
    }),
  );

  const otherResourcesRequired = [
    "FACILITY_AND_STAFF",
    "STAFF_AND_FACILITY",
  ].includes(appointable.availabilityType)
    ? "YES"
    : "NO";

  return {
    details: {
      name: appointable.name,
      category: categoryOption,
      duration: durationOption,
      blockExtraTime: blockExtraTimeOption,
      bookingPossibleEvery: bookingPossibleEveryOption,
      isMembersOnly: appointable.forMembersOnly,
      isTopOfTheHour: appointable.slotAlignment === "HOUR_START",
      description: appointable.description || "",
      appointmentTerms: appointable.termsAndConditions || "",
    },
    pricingStructure: {
      pricingStructure,
      isUpfrontPaymentRequired: appointable.paymentUpfront,
      typeOfCost,
      credits: appointable.sessionCost ?? 0,
      price: {
        exTaxPrice: priceExTaxPrice,
        incTaxPrice: appointable.price ?? 0,
        salesTaxRate: appointable.taxRate ?? 0,
      },
      isShowMembersPrice:
        appointable.membersOnlyPrice === undefined ? false : true,
      membersPrice: {
        exTaxPrice: membersPriceExTaxPrice,
        incTaxPrice: appointable.membersOnlyPrice ?? 0,
      },
    },
    resources: {
      resourceRequired:
        appointable.availabilityType === "FACILITY_AND_STAFF" ||
        appointable.availabilityType === "FACILITY"
          ? "FACILITY"
          : "STAFF",
      appointmentHostsStaff: staffOptions,
      appointmentHostsFacility: facilityOptions,
      otherResourcesRequired,
      otherResourcesFacility:
        otherResourcesRequired === "YES" &&
        appointable.availabilityType === "STAFF_AND_FACILITY"
          ? facilityOptions
          : [],
      otherResourcesStaff:
        otherResourcesRequired === "YES" &&
        appointable.availabilityType === "FACILITY_AND_STAFF"
          ? staffOptions
          : [],
    },
  };
};
